import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useKid } from "@/context/KidContext";
import { toast } from "sonner";
import { CheckCircle2, PlayCircle } from "lucide-react";

export default function JourneyDetail() {
  const { key } = useParams();
  const { active, reloadKids } = useKid();
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = active ? { child_id: active.id } : {};
      const { data } = await api.get(`/journeys/${key}`, { params });
      setJourney(data.journey);
      setProgress(data.progress);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/journeys");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [key, active]);

  const start = async () => {
    if (!active) return toast.error("Selecione uma criança no perfil");
    setBusy(true);
    try {
      const { data } = await api.post(`/journeys/${key}/start`, { child_id: active.id });
      setProgress(data.progress);
      toast.success(data.already_started ? "Continuando de onde parou" : "Jornada iniciada 💛");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const completeStep = async (step_number) => {
    if (!active) return toast.error("Selecione uma criança no perfil");
    setBusy(true);
    try {
      const { data } = await api.post(`/journeys/${key}/complete-step`, { child_id: active.id, step_number });
      setProgress(data.progress);
      if (data.awarded) {
        toast.success("Etapa concluída! Pontos e estrelas creditados 🌟");
        await reloadKids();
      } else {
        toast("Etapa já estava concluída.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!journey) return <div className="p-10 text-center text-[#243B53]/60">Carregando…</div>;

  const done = new Set(progress?.completed_step_numbers || []);
  const total = journey.steps?.length || 0;
  const doneCount = done.size;
  const completed = !!progress?.completed_at;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="journey-detail-page">
      <Link to="/journeys" className="text-[#5DADE2] font-bold text-sm">← Todas as jornadas</Link>
      <div className="mt-4 card-soft overflow-hidden">
        <div className="aspect-[21/9] bg-[#FFF9ED]">
          {journey.cover_image && <img src={journey.cover_image} alt={journey.title} className="w-full h-full object-cover"/>}
        </div>
        <div className="p-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#243B53]">{journey.title}</h1>
          <p className="mt-2 text-[#243B53]/75">{journey.description}</p>
          <div className="mt-4 flex gap-3 flex-wrap">
            <span className="chip chip-sun">{total} etapas</span>
            <span className="chip">{journey.duration_days} dias</span>
            {journey.age_groups?.map(a => <span key={a} className="chip chip-leaf">{a} anos</span>)}
          </div>

          {completed ? (
            <div className="mt-6 chip chip-gold" data-testid="journey-completed">🎉 Jornada concluída!</div>
          ) : !progress?.started_at ? (
            <button data-testid="journey-start-btn" onClick={start} disabled={busy} className="btn-primary mt-6">
              {busy ? "Aguarde…" : "Iniciar jornada"}
            </button>
          ) : (
            <div className="mt-6">
              <div className="text-xs font-bold text-[#243B53]/60">{doneCount}/{total} etapas concluídas</div>
              <div className="mt-1 progress-track"><div className="progress-fill" style={{ width: `${(doneCount/Math.max(1,total))*100}%` }}/></div>
            </div>
          )}
        </div>
      </div>

      <section className="mt-8">
        <h2 className="text-xl font-extrabold text-[#243B53]">Etapas</h2>
        <ol className="mt-4 space-y-3">
          {(journey.steps || []).map((s, i) => {
            const isDone = done.has(s.step_number);
            return (
              <li key={s.step_number} data-testid={`journey-step-${s.step_number}`} className="card-soft p-4 flex gap-4 items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-extrabold ${isDone ? "bg-[#7BC67B] text-white" : "bg-[#FFD54F] text-[#243B53]"}`}>
                  {isDone ? <CheckCircle2 size={22} /> : s.step_number}
                </div>
                <div className="flex-1">
                  <div className="font-extrabold text-[#243B53]">{s.title}</div>
                  {s.description && <div className="text-sm text-[#243B53]/70">{s.description}</div>}
                  <div className="text-xs text-[#243B53]/60 mt-1">+{s.points_awarded} pts • +{s.stars_awarded} ⭐</div>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  {s.content_id && (
                    <Link to={`/content/${s.content_id}`} className="btn-outline text-sm inline-flex items-center gap-1">
                      <PlayCircle size={16}/> Abrir
                    </Link>
                  )}
                  {!isDone && progress?.started_at && (
                    <button data-testid={`journey-step-complete-${s.step_number}`} onClick={() => completeStep(s.step_number)} disabled={busy} className="btn-primary text-sm">
                      Marcar feita
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}
